<script lang="ts">
import KontrolUi from 'components/Kontrol/index.vue';
import TheResponsiveMagic from 'components/Responsive/index.vue';
import SmoothScroll from 'components/SmoothScroll/index.vue';
import { store as themeStore } from 'pages/Thematique/store';
import SEO from 'plugins/seo/index.vue';
import { store as interfaceStore } from 'plugins/store/interface';
import Transitions from 'src/mixins/transitions';
import appStore from 'store/app';
import loaderStore from 'ui/Loader/store';
import { computed, defineAsyncComponent, defineComponent, toRefs, watch } from 'vue';
// import TheDebugBar from 'ui/DebugBar/index.vue';
import TheHeaderCredit from 'pages/Credit/blocks/Header.vue';
import TheHeaderThematique from 'pages/Thematique/blocks/Header.vue';
import TheHeader from 'ui/Header/index.vue';
import TheLoader from 'ui/Loader/index.vue';
// import i18nStore from 'plugins/i18n'
//import TheCursor from 'UiKit/Cursor/index.vue';
import { tryOnMounted, until } from '@vueuse/core';
import TheFlashMessage from 'UiKit/FlashMessage/index.vue';
import InactivityScreen from 'components/ui/Inactivity/index.vue';
import MobileScreen from 'components/ui/Mobile/index.vue';
import emitter, { EVENTS } from 'plugins/store/events';
import { useRoute } from 'vue-router';


const async404 = defineAsyncComponent(
  () => import(/* webpackChunkName: "page-404" */'pages/FourOFour.vue')
)

export default defineComponent({
  name: "TheApp",
  components: {
    SEO,
    TheHeader,
    TheLoader,
    TheFlashMessage,
    TheHeaderThematique,
    TheHeaderCredit,
    // TheDebugBar,
    SmoothScroll,
    TheResponsiveMagic,
    KontrolUi,
    MobileScreen,
    InactivityScreen
  },
  setup() {
    const { beforeEnterTransition, enterTransition, leaveTransition, ThePageTransition } = Transitions();

    const { viewport } = toRefs(interfaceStore.state);
    const { is404 } = toRefs(appStore.state);
    const { done } = toRefs(loaderStore.state);
    const isMobile = computed(() => !(interfaceStore.isLayoutDesktop));
    const route = useRoute();

    let timer: any = null;

    tryOnMounted(async () => {
      emitter.on(EVENTS.STOP_INACTIVE, stopTimer);
      emitter.on(EVENTS.RESET_INACTIVE, startTimer);

      watch(
        isMobile,
        mobile => {
          if (mobile) {
            stopTimer();
          }
          else {
            startTimer();
          }
        },
        {
          immediate: true
        }
      );
      
      //detect if it's the install mode or not
      if ((window as any)?.INSTALL) {
        interfaceStore.setInstallMode();
        document.documentElement.classList.add('-install');
      }

      if((window as any)?.REDIRECT) {
        appStore.setRedirectionMode();
      }

      await until(done).toBe(true);

    });

    const startTimer = () => {
      window.addEventListener("pointermove", onActive);

      onActive();
    }

    const stopTimer = () => {
      window.removeEventListener("pointermove", onActive);

      clearTimeout(timer);
    }

    const onActive = () => {
      clearTimeout(timer);

      timer = setTimeout(() => {
        appStore.toggleInactive(true);
      }, 2 * 60 * 1000);
    }

    const onResetTimer = () => {
      appStore.toggleInactive(false);
      onActive();
    }

    return {
      doneLoader: loaderStore.state,
      // readyToRenderView: i18nStore.ready,
      /** ready when app & global
       * but inside you may wait data etc
       * need to render before data to trigger load ;) */
      readyToRenderView: computed(() => {
        return appStore.state.ready.app && appStore.state.ready.globals && appStore.state.ready.interface
      }),
      is404,
      beforeEnterTransition,
      enterTransition,
      leaveTransition,
      ThePageTransition,
      async404,
      isMobile,
      currentVariant: computed(() => themeStore.current?.id || "default"),
      isThemeRoute: computed(() => route.name === 'thematique'),
      isCreditRoute: computed(() => route.name === 'credit'),
      showInactivity: computed(() => appStore.isInactive),
      viewport: viewport,
      onResetTimer
    }
  },
});
</script>

<template>
  <SEO />
  <TheHeader v-if="readyToRenderView && !isMobile" />
  <TheHeaderThematique v-if="readyToRenderView && isThemeRoute && !isMobile" />
  <TheHeaderCredit v-if="readyToRenderView && isCreditRoute && !isMobile" />

  <SmoothScroll v-if="readyToRenderView">
    <div class="page-wrapper">
      <router-view v-slot="{ Component, route }">
        <transition :css="false"
                    appear
                    mode="out-in"
                    @before-enter="beforeEnterTransition"
                    @enter="enterTransition"
                    @leave="leaveTransition"
        >
          <!-- PATCH : Force render 404 if needed
            @see: https://www.notion.so/kffein/VUE-Render-update-issues-11535112181140d8b5fb871823883ba7
          -->
          <div v-if="readyToRenderView || is404"
               :key="is404 ? '404' : route.path"
               class="page-container"
          >
            <MobileScreen v-if="isMobile" />
            <component :is="is404 ? async404 : Component"
                       v-else
                       class="page"
            />
          </div>
        </transition>
      </router-view>
    </div>
    <InactivityScreen v-if="showInactivity" @continue="onResetTimer" />
  </SmoothScroll>

  <div class="webgl-wrapper" />


  <TheFlashMessage />
  <TheLoader v-if="readyToRenderView" ref="ThePageTransition" />
  <component :is="'style'">
    :root {
    --viewport-height: {{ viewport.height }}px;
    --viewport-innerHeight: {{ viewport.innerHeight }}px;
    --viewport-webglHeight: {{ viewport.webglHeight }}px;
    --variant: {{ currentVariant }}
    }
  </component>
  <!-- <TheDebugBar /> -->
  <TheResponsiveMagic />
  <KontrolUi />

  <!-- safe zone outside scroll - usefull to teleport -->
  <div class="teleport" />
  <div class="transitioning" />
</template>

<style lang="stylus">
  @import '../assets/styles/index'

  ::v-global(.grecaptcha-badge)
    display none !important
    visibility hidden !important
    opacity 0 !important
    pointer-events none !important

  .webgl-wrapper
    fixed top 0 left 0
    size 100vw var(--viewport-innerHeight)
    z-index -1
    pointer-events none
    overflow hidden
    +layout(mobile)
      height 100vmax

  .-webgl-hide, :deep(.-webgl-hide)
    opacity 0

</style>
